import React from "react";
import "./styles/Home.css";
import Footer from "./Footer";
import Header from "./Header";

function PrivacyPolicy() {
  return (
    <div className="privacy-policy bg-background min-h-screen">
      <Header />

      <main className="privacy-policy-main mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl mt-4 mb-4">
        <h1 className="text-xl font-bold mb-6">Privacy Policy</h1>

        <p className="mt-2 text-base">
          At RealignMind.com, operated by Realign LLC ("we," "us," or "our"), we
          value your privacy and are committed to protecting your personal
          information. This Privacy Policy explains how we collect, use, and
          safeguard your data when you use our website, mobile application, and
          related services (collectively "the Platform").
        </p>

        <h2 className="text-lg font-semibold mt-4">
          1. Information We Collect
        </h2>
        <p className="mt-2 text-base">
          We collect the following types of information:
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li>
            <strong>Personal Information:</strong> This includes your name,
            email address, and other identifying information you provide when
            creating an account, contacting us, or using the Platform.
          </li>
          <li>
            <strong>Usage Data:</strong> Information about how you interact with
            the Platform, such as session activity, IP addresses, and device
            details.
          </li>
          <li>
            <strong>Cookies:</strong> Small text files stored on your device to
            enhance your user experience and track your preferences.
          </li>
        </ul>

        <h2 className="text-lg font-semibold mt-4">
          1A. Health and Wellness Information
        </h2>
        <p className="mt-2 text-base">
          <strong>Sensitive Personal Information:</strong> We collect and process health and wellness-related information, including:
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li><strong>Session Data:</strong> Records of your wellness sessions, including EMDR-style technique usage, session duration, and frequency</li>
          <li><strong>Wellness Tracking:</strong> Mood indicators, stress levels, and self-reported wellness metrics</li>
          <li><strong>Platform Interaction:</strong> How you use specific wellness features, progress tracking, and engagement patterns</li>
          <li><strong>Device Information:</strong> Technical data from your interactions with wellness content</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Special Protections:</strong> We treat all wellness-related information with enhanced security measures and do not share this information with third parties except as specifically disclosed in this policy or as required by law.
        </p>
        <p className="mt-2 text-base">
          <strong>No Medical Advice:</strong> Our platform provides wellness tools and is not intended to diagnose, treat, cure, or prevent any medical condition. We are not a covered entity under HIPAA, but we apply similar privacy protections to your wellness information.
        </p>

        <h2 className="text-lg font-semibold mt-4">
          2. How We Use Your Information
        </h2>
        <p className="mt-2 text-base">
          We use your information for the following purposes:
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li>To provide and improve our services.</li>
          <li>
            To communicate with you regarding updates, promotions, or support.
          </li>
          <li>
            To monitor and analyze usage patterns to improve the Platform.
          </li>
          <li>
            To ensure compliance with our Terms of Service and applicable laws.
          </li>
        </ul>

        <h2 className="text-lg font-semibold mt-4">
          2A. Specific Business Purposes
        </h2>
        <p className="mt-2 text-base">
          We use your information for these specific business purposes:
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li><strong>Service Delivery:</strong> Providing access to wellness tools and tracking your progress</li>
          <li><strong>Platform Improvement:</strong> Analyzing usage patterns to enhance our wellness features</li>
          <li><strong>Safety and Security:</strong> Monitoring for platform abuse and ensuring user safety</li>
          <li><strong>Customer Support:</strong> Responding to your questions and technical issues</li>
          <li><strong>Legal Compliance:</strong> Meeting our obligations under applicable privacy and business laws</li>
          <li><strong>Crisis Resources:</strong> Providing access to mental health resources when requested</li>
        </ul>
        <p className="mt-2 text-base">
          We do not use your wellness information for automated decision-making that significantly affects you without your explicit consent.
        </p>

        <h2 className="text-lg font-semibold mt-4">3. Detailed Third-Party Disclosures</h2>
        <p className="mt-2 text-base">
          We do not sell, rent, or lease your personal information. We may share your information only with:
        </p>
        <p className="mt-2 text-base">
          <strong>Service Providers:</strong>
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li><strong>DigitalOcean (Hosting):</strong> Secure cloud hosting and data storage</li>
          <li><strong>Stripe (Payment Processing):</strong> Subscription billing and payment processing - they do not receive your wellness data</li>
          <li><strong>Google Analytics:</strong> Website usage analytics using anonymized data only</li>
          <li><strong>Customer Support Tools:</strong> For responding to your service requests</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Required Disclosures:</strong>
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li><strong>Legal Process:</strong> Court orders, subpoenas, or other legal requirements</li>
          <li><strong>Safety Protection:</strong> To prevent harm to you, other users, or the public</li>
          <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of our business</li>
          <li><strong>Consent:</strong> Any other sharing requires your explicit permission</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Data Processing Agreements:</strong> All service providers are contractually required to protect your information and use it only for specified purposes.
        </p>

        <h2 className="text-lg font-semibold mt-4">4. Enhanced Data Security</h2>
        <p className="mt-2 text-base">
          <strong>Security Measures:</strong>
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li><strong>Encryption:</strong> All data is encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
          <li><strong>Access Controls:</strong> Strict limitations on who can access your personal information</li>
          <li><strong>Monitoring:</strong> Continuous monitoring for unauthorized access attempts</li>
          <li><strong>Regular Audits:</strong> Periodic security assessments and penetration testing</li>
          <li><strong>Incident Response:</strong> Established procedures for responding to potential security breaches</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Data Breach Notification:</strong> In the unlikely event of a data breach affecting your personal information, we will:
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li>Notify affected users within 72 hours via email</li>
          <li>Provide details about what information was involved</li>
          <li>Explain steps we're taking to address the breach</li>
          <li>Offer guidance on protecting yourself from potential harm</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Limitations:</strong> While we implement industry-standard security measures, no method of electronic storage or transmission is 100% secure. We cannot guarantee absolute security but commit to promptly addressing any security concerns.
        </p>

        <h2 className="text-lg font-semibold mt-4">5. Your Rights</h2>
        <p className="mt-2 text-base">You have the right to:</p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li>Access the personal information we hold about you.</li>
          <li>Request corrections to your information.</li>
          <li>Delete your account and associated data.</li>
          <li>Opt out of non-essential communications.</li>
        </ul>

        <h2 className="text-lg font-semibold mt-4">
          5A. California Consumer Privacy Rights
        </h2>
        <p className="mt-2 text-base">
          <strong>California Residents:</strong> Under the California Consumer Privacy Act (CCPA), you have specific rights:
        </p>
        <p className="mt-2 text-base">
          <strong>Right to Know:</strong>
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li>Categories of personal information we collect</li>
          <li>Sources of personal information</li>
          <li>Business purposes for collecting information</li>
          <li>Categories of third parties with whom we share information</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Right to Delete:</strong> Request deletion of your personal information, subject to legal exceptions
        </p>
        <p className="mt-2 text-base">
          <strong>Right to Opt-Out:</strong> We do not sell personal information, but you can opt-out of future sales
        </p>
        <p className="mt-2 text-base">
          <strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your privacy rights
        </p>
        <p className="mt-2 text-base">
          <strong>Exercising Your Rights:</strong>
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li><strong>Online:</strong> Use your account dashboard to download or delete your data</li>
          <li><strong>Email:</strong> Contact privacy@realignmind.com with your request</li>
          <li><strong>Verification:</strong> We may require identity verification for security purposes</li>
          <li><strong>Response Time:</strong> We will respond within 30 days of receiving your request</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Authorized Agents:</strong> You may designate an authorized agent to make requests on your behalf with proper documentation.
        </p>

        <h2 className="text-lg font-semibold mt-4">6. Third-Party Links</h2>
        <p className="mt-2 text-base">
          The Platform may contain links to third-party websites or services. We
          are not responsible for the privacy practices or content of these
          external sites. We encourage you to review their privacy policies
          before providing any personal information.
        </p>

        <h2 className="text-lg font-semibold mt-4">7. Data Retention Policy</h2>
        <p className="mt-2 text-base">
          <strong>Retention Periods:</strong>
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li><strong>Account Information:</strong> Retained until you delete your account, then permanently removed within 30 days</li>
          <li><strong>Session Data:</strong> Retained for active users indefinitely; deleted 24 months after account becomes inactive</li>
          <li><strong>Payment Records:</strong> Retained for 7 years for tax and accounting purposes</li>
          <li><strong>Support Communications:</strong> Retained for 3 years from last contact</li>
          <li><strong>Marketing Data:</strong> Retained for 2 years or until you opt-out</li>
        </ul>
        <p className="mt-2 text-base">
          <strong>Automated Deletion:</strong> We use automated systems to delete information according to these schedules
        </p>
        <p className="mt-2 text-base">
          <strong>User Control:</strong> You can request immediate deletion of your account and associated data at any time through your dashboard or by contacting us
        </p>
        <p className="mt-2 text-base">
          <strong>Legal Holds:</strong> Data may be retained longer if required by legal proceedings or regulatory investigations
        </p>

        <h2 className="text-lg font-semibold mt-4">7A. International Users</h2>
        <p className="mt-2 text-base">
          <strong>Data Transfers:</strong> Your information may be transferred to and processed in the United States, where our servers are located
        </p>
        <p className="mt-2 text-base">
          <strong>Adequate Protection:</strong> We ensure appropriate safeguards are in place for international data transfers
        </p>
        <p className="mt-2 text-base">
          <strong>European Users:</strong> If you are in the European Economic Area, you have additional rights under the General Data Protection Regulation (GDPR), including:
        </p>
        <ul className="list-disc list-inside mt-2 text-base">
          <li>Right to data portability</li>
          <li>Right to restrict processing</li>
          <li>Right to object to processing</li>
          <li>Right to lodge complaints with supervisory authorities</li>
        </ul>

        <h2 className="text-lg font-semibold mt-4">8. Children's Privacy</h2>
        <p className="mt-2 text-base">
          <strong>Age Restrictions:</strong> Our platform is not intended for children under 18 years of age
        </p>
        <p className="mt-2 text-base">
          <strong>Parental Consent:</strong> If you are under 18, you must have parental permission to use our services
        </p>
        <p className="mt-2 text-base">
          <strong>Data Collection:</strong> We do not knowingly collect personal information from children under 13
        </p>
        <p className="mt-2 text-base">
          <strong>Removal:</strong> If we learn we have collected information from a child under 13, we will delete it immediately
        </p>

        <h2 className="text-lg font-semibold mt-4">9. Contact Information for Privacy Matters</h2>
        <p className="mt-2 text-base">
          <strong>Privacy Questions:</strong> For questions about this Privacy Policy or your personal information:
        </p>
        <p className="mt-2 text-base">
          <strong>Email:</strong> privacy@realignmind.com<br />
          <strong>Response Time:</strong> We will respond to privacy inquiries within 5 business days<br />
          <strong>California Residents:</strong> You may also contact us using the information above to exercise your CCPA rights<br />
          <strong>Data Protection Officer:</strong> Jed Reisner, CEO, privacy@realignmind.com
        </p>

        <h2 className="text-lg font-semibold mt-4">
          10. Changes to This Policy
        </h2>
        <p className="mt-2 text-base">
          We may update this Privacy Policy from time to time. Any changes will
          be posted on this page with the updated "Last Updated" date. By
          continuing to use the Platform, you agree to the updated Privacy
          Policy.
        </p>

        <p className="mt-2 text-base">
          <strong>Legal Disclaimer:</strong> This Privacy Policy is governed by the laws of Utah. If any provision is found unenforceable, the remainder shall remain in full force and effect. This policy supplements but does not replace any other agreements between you and RealignMind.
        </p>

        <p className="mt-2 text-base">Last Updated: June 11, 2025</p>
      </main>

      <Footer />
    </div>
  );
}

export default PrivacyPolicy;
